// ============================================================
// chatFormat — turn one Harvi message's PLAIN TEXT into render blocks.
//
// WHY THIS EXISTS: the transcript used to render every message as one
// `white-space: pre-wrap` string (web) or one `<Text>` (iOS). A four-paragraph
// answer with a bulleted list in it therefore arrived as an undifferentiated
// wall — the single biggest thing making a transcript hard to read, and the
// reason a number quoted three lines down was easy to miss.
//
// TABLES ARE THE ONE BLOCK WHOSE ABSENCE ACTIVELY DESTROYS AN ANSWER, rather
// than merely under-formatting it. Every other unparsed construct still reads
// as the line the model wrote; a table does not, because the paragraph rule
// below joins consecutive non-blank lines with a space — so a twenty-row
// filing plan arrived as one unbroken `| 1 | Re: … | | 2 | Completed: … |`
// sentence running the width of the panel, with the header, the `|---|---|`
// separator and every row fused together. That was not a table rendered
// plainly; it was a table rendered as noise. Models reach for a table exactly
// when the answer has repeating fields per row, which is when a reader most
// needs the columns, so this is parsed even though it is the most involved
// block here.
//
// IT IS DELIBERATELY NOT A FULL MARKDOWN RENDERER. What is honoured is
// structure the model genuinely emits and that a reader loses without it:
// paragraphs, bullets, numbered steps, headings, fenced code, thematic breaks,
// tables, and inline emphasis/code. Rendering `**foo**` as bold is strictly
// better than showing a reader the asterisks.
//
// LINKS ARE AN OPTION, NOT A DEFAULT, AND THAT IS THE ONE PLACE THE TWO
// CLIENTS DIVERGE ON PURPOSE — so the divergence is a named argument here
// rather than two parsers drifting apart. The web transcript cites its records
// through the server-derived `records` chips beside the message; turning prose
// into anchors there would be exactly what ChatWidget stopped doing when the
// legacy bot's markdown deep links were dropped — inventing navigation out of
// text a model composed. The iOS bubble has always made a plain `https://…`
// tappable, which is a different thing: it is the OS opening a URL a human can
// read in full, not the app resolving an id a model guessed. So `links` is off
// unless a caller asks, and in-app records never come through here on either
// client.
//
// Pure — no DOM, no React, no React Native. Unit-tested in
// frontend/src/__tests__/chatFormat.test.js (both clients import this file, so
// one suite covers both).
// ============================================================

export type Align = "left" | "center" | "right";

/** One styled span of a line. `href` appears only when `links` is enabled. */
export interface Run {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  href?: string;
}

export interface TextBlock {
  type: "paragraph" | "bullet" | "numbered" | "heading";
  runs: Run[];
  /** `numbered` only — the model's own marker, never a renumbering of ours. */
  marker?: string;
  /** `heading` only — 1-4. */
  level?: number;
}
export interface CodeBlock { type: "code"; text: string }
export interface RuleBlock { type: "rule" }
export interface TableBlock {
  type: "table";
  header: Run[][];
  rows: Run[][][];
  align: (Align | null)[];
}
export type MessageBlock = TextBlock | CodeBlock | RuleBlock | TableBlock;

export interface ParseOptions {
  /** Make `[label](url)` and bare `https://…` tappable runs. Default false. */
  links?: boolean;
}

const BULLET_RE = /^\s{0,4}[-*•]\s+(.*)$/;
const NUMBER_RE = /^\s{0,4}(\d{1,2})[.)]\s+(.*)$/;
const HEADING_RE = /^\s{0,3}(#{1,4})\s+(.*)$/;
const FENCE_RE = /^\s*```/;
// A thematic break. Models put one between sections constantly, and rendered as
// the literal text "---" it reads as a mistake rather than as a divider.
const RULE_RE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
// One cell of a table's `|---|:--:|` separator row. Alignment colons included,
// because a column of figures aligned left is harder to compare than one that
// is not aligned at all.
const DELIM_CELL_RE = /^:?-+:?$/;

// Inline emphasis. Split on the whole token so the delimiters never survive
// into the rendered text, and so an unmatched `**` is left alone rather than
// swallowing the rest of the paragraph.
//
// BOLD IS LISTED BEFORE ITALIC and the italic body may not begin or end with a
// space. Both rules exist to stop `*` being claimed where it is not emphasis:
// without the ordering `**foo**` would be read as an italic `*foo*` wrapped in
// stray asterisks, and without the space rule an arithmetic `3 * 4 * 5` would
// italicise its way across the sentence. Deliberately NO `_underscore_` form —
// underscores are everywhere in real file names, folder paths and column names
// in this book (`Projects_2024`, `Tax_Lien_Release`), so honouring them would
// mangle far more text than it would style.
const EMPHASIS = "\\*\\*[^*\\n]+\\*\\*|\\*[^\\s*](?:[^*\\n]*[^\\s*])?\\*|`[^`\\n]+`";
// A markdown link is matched BEFORE a bare URL so the bare-URL alternative
// cannot claim the `(…)` half of one and leave `[label](` as stray text.
const LINKS = "\\[[^\\]\\n]+\\]\\([^)\\s]+\\)|https?:\\/\\/[^\\s]+";

const inlineRe = (links: boolean) =>
  new RegExp(`(${links ? `${LINKS}|` : ""}${EMPHASIS})`, "g");

// Sentence punctuation that ends a sentence rather than a URL. A closing paren
// is deliberately NOT trimmed — real URLs carry them (Wikipedia, SharePoint).
const trimUrl = (url: string) => url.replace(/[.,;:!?]+$/, "");

/** Split one line into styled runs. Returns [{ text, bold?, italic?, code?, href? }]. */
export const inlineRuns = (line: string | null | undefined, opts: ParseOptions = {}): Run[] => {
  const src = String(line ?? "");
  const out: Run[] = [];
  const push = (text: string, extra: Partial<Run> = {}) => { if (text) out.push({ text, ...extra }); };
  let last = 0;
  for (const m of src.matchAll(inlineRe(opts.links === true))) {
    const at = m.index ?? 0;
    push(src.slice(last, at));
    const token = m[0];
    if (token.startsWith("`")) push(token.slice(1, -1), { code: true });
    else if (token.startsWith("**")) push(token.slice(2, -2), { bold: true });
    else if (token.startsWith("*")) push(token.slice(1, -1), { italic: true });
    else if (token.startsWith("[")) {
      const split = token.indexOf("](");
      push(token.slice(1, split), { href: token.slice(split + 2, -1) });
    } else {
      // A bare URL. Punctuation the sentence owns is handed back as text so it
      // is neither swallowed by the link nor lost.
      const url = trimUrl(token);
      push(url, { href: url });
      push(token.slice(url.length));
    }
    last = at + token.length;
  }
  push(src.slice(last));
  return out.length ? out : [{ text: "" }];
};

/**
 * Split one table line into trimmed cell strings.
 * The outer pipes are optional in GFM and a `\|` inside a cell is a literal
 * pipe — which matters here, since a cell of this table is routinely a mail
 * subject somebody else wrote and may contain anything at all.
 */
export const splitTableRow = (line: string | null | undefined): string[] => {
  let s = String(line ?? "").trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cell = "";
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === "\\" && s[i + 1] === "|") { cell += "|"; i += 1; continue; }
    if (s[i] === "|") { cells.push(cell.trim()); cell = ""; continue; }
    cell += s[i];
  }
  cells.push(cell.trim());
  return cells;
};

/**
 * Is this the `|---|---|` row directly under a header?
 * It must carry a pipe: without that test a lone `---` — a thematic break,
 * which is far commoner — would turn whatever paragraph line preceded it into
 * a one-row table.
 */
const isDelimiterRow = (line: string | undefined): boolean => {
  const raw = String(line ?? "");
  if (!raw.includes("|")) return false;
  const cells = splitTableRow(raw);
  return cells.length > 0 && cells.every((c) => DELIM_CELL_RE.test(c));
};

const alignOf = (cell: string): Align | null => {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
};

/**
 * Assemble a table block from its parts.
 * WIDTH IS THE WIDEST ROW, NEVER THE HEADER'S. GFM says a row longer than the
 * header is truncated; here that would silently drop a cell of somebody's data
 * because a model miscounted its own pipes, so every row is padded out to the
 * widest one instead and nothing written is thrown away.
 */
const tableBlock = (
  headerCells: string[], delimCells: string[], rowCells: string[][], opts: ParseOptions,
): TableBlock => {
  const width = Math.max(headerCells.length, 1, ...rowCells.map((r) => r.length));
  const pad = (cells: string[]) =>
    Array.from({ length: width }, (_, i) => inlineRuns(cells[i] ?? "", opts));
  return {
    type: "table",
    align: Array.from({ length: width }, (_, i) => alignOf(delimCells[i] ?? "")),
    header: pad(headerCells),
    rows: rowCells.map(pad),
  };
};

/**
 * Parse a message body into blocks:
 *   { type: 'paragraph' | 'bullet' | 'numbered' | 'heading', runs, marker?, level? }
 *   { type: 'code', text }
 *   { type: 'rule' }
 *   { type: 'table', header, rows, align }
 * Consecutive non-blank plain lines join into one paragraph (a soft wrap in the
 * source is not a paragraph break), and blank lines separate blocks.
 */
export const parseMessageBody = (
  text: string | null | undefined, opts: ParseOptions = {},
): MessageBlock[] => {
  const src = String(text ?? "");
  if (!src.trim()) return [];
  const lines = src.split(/\r?\n/);
  const blocks: MessageBlock[] = [];
  let para: string[] = [];
  let fence: string[] | null = null;   // collected code lines while inside a ``` fence

  const flushParagraph = () => {
    if (!para.length) return;
    blocks.push({ type: "paragraph", runs: inlineRuns(para.join(" "), opts) });
    para = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    if (FENCE_RE.test(raw)) {
      if (fence === null) { flushParagraph(); fence = []; }
      else { blocks.push({ type: "code", text: fence.join("\n") }); fence = null; }
      continue;
    }
    if (fence !== null) { fence.push(raw); continue; }

    if (!raw.trim()) { flushParagraph(); continue; }

    // A TABLE IS TESTED FIRST, because its header row can look like something
    // else: `| # | Subject |` is fine, but a model that omits the leading pipe
    // writes `# | Subject |`, which HEADING_RE claims. Requiring the separator
    // row underneath is what makes reading ahead safe — nothing else in this
    // grammar has one.
    if (raw.includes("|") && isDelimiterRow(lines[i + 1])) {
      flushParagraph();
      const header = splitTableRow(raw);
      const delim = splitTableRow(lines[i + 1]);
      const rows: string[][] = [];
      let j = i + 2;
      // The table ends at the first blank line or the first line with no pipe
      // in it — prose resuming under the table is not a row of it.
      for (; j < lines.length && lines[j].trim() && lines[j].includes("|"); j += 1) {
        rows.push(splitTableRow(lines[j]));
      }
      blocks.push(tableBlock(header, delim, rows, opts));
      i = j - 1;
      continue;
    }

    if (RULE_RE.test(raw)) { flushParagraph(); blocks.push({ type: "rule" }); continue; }

    const heading = HEADING_RE.exec(raw);
    if (heading) {
      flushParagraph();
      blocks.push({ type: "heading", level: heading[1].length, runs: inlineRuns(heading[2], opts) });
      continue;
    }
    const bullet = BULLET_RE.exec(raw);
    if (bullet) {
      flushParagraph();
      blocks.push({ type: "bullet", runs: inlineRuns(bullet[1], opts) });
      continue;
    }
    const numbered = NUMBER_RE.exec(raw);
    if (numbered) {
      flushParagraph();
      blocks.push({ type: "numbered", marker: `${numbered[1]}.`, runs: inlineRuns(numbered[2], opts) });
      continue;
    }
    para.push(raw.trim());
  }
  // An unterminated fence is still content — emit what was collected rather
  // than dropping the tail of a streaming answer mid-block.
  if (fence !== null && fence.length) blocks.push({ type: "code", text: fence.join("\n") });
  flushParagraph();
  return blocks;
};

export interface ColumnWidthOptions {
  charWidth?: number;
  min?: number;
  max?: number;
  padding?: number;
}

/**
 * Pick a pixel width per table column.
 *
 * THIS EXISTS FOR REACT NATIVE, WHICH HAS NO TABLE LAYOUT ALGORITHM. On the web
 * the browser sizes columns from their content for free; RN has flexbox only,
 * so every column is either an equal share of the row — which gives a `#`
 * column the same space as a mail subject — or a width chosen up front. This
 * chooses it, from the longest cell in each column, clamped at both ends: `min`
 * so a one-character column is still tappable and legible, `max` so one verbose
 * cell cannot make the horizontal scroll endless (past it the cell wraps, which
 * is what the reader wants anyway).
 *
 * It is a heuristic over an average character width, not a measurement, so it
 * is approximate by construction — which is fine, because being a little wide
 * or narrow costs nothing while getting no answer at all costs the table.
 */
export const tableColumnWidths = (
  table: Pick<TableBlock, "header" | "rows">, opts: ColumnWidthOptions = {},
): number[] => {
  const { charWidth = 7.2, min = 46, max = 210, padding = 22 } = opts;
  const len = (cell: Run[] | undefined) => (cell || []).reduce((n, r) => n + r.text.length, 0);
  return table.header.map((_, c) => {
    const longest = Math.max(len(table.header[c]), ...table.rows.map((r) => len(r[c])), 0);
    return Math.round(Math.min(max, Math.max(min, longest * charWidth + padding)));
  });
};

export default parseMessageBody;
